import React, { useEffect, useState } from "react";

import { Box, CircularProgress, TextField } from "@mui/material";

import { useDispatch, useSelector } from "react-redux";

import {
    getLeadGoogleFields,
    getLeadGoogleSourceDetail,
    postLeadGoogleSource,
    updateLeadGoogleSource,
} from "helpers/leads/leadSources";

import { GenericModal } from "components/GenericModal";
import {
    CreationFooter,
    creationFooterSx,
} from "components/organisms/CreationModal";
import DynamicFieldTable from "components/organisms/DynamicFieldTable";

import {
    getAllLeadSourcesAsync,
    getGoogleConnectedSources,
} from "../../../redux/slices/leadSources/leadSources.asyncActions";
import { lastAddedWizzardSource } from "../../../redux/slices/leadsWizardSourceDestination/leadsWizardSourceDestination.slice";
import { closeModal } from "../../../redux/slices/modal/modal.slice";

const AddEditGoogleSource = ({ data }) => {
    const isEditMode = !!Object.keys(data)?.length;

    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(false);
    const [isModalReady, setIsModalReady] = useState(false);
    const [name, setName] = useState("");
    const [fields, setFields] = useState([]);

    const language = useSelector((state) => state.languagesState.languages);

    const handleClose = () => dispatch(closeModal());

    const editFunc = async (payload) => {
        setIsLoading(true);

        const response = await updateLeadGoogleSource(data?.id, payload);

        if (response.status) {
            dispatch(getAllLeadSourcesAsync());
            dispatch(closeModal());
        }

        setIsLoading(false);
    };

    const createFunc = async (payload) => {
        setIsLoading(true);

        const response = await postLeadGoogleSource(payload);

        if (response.status) {
            dispatch(lastAddedWizzardSource(response.data ?? ""));
            dispatch(getGoogleConnectedSources());
            dispatch(closeModal());
        }

        setIsLoading(false);
    };

    const handleClickCreateBtn = () => {
        const createData = {
            name,
            fields: fields
                ?.filter((item) => !item?.disabled)
                ?.map((item) => item?.field?.value)
                ?.filter((item) => item),
        };

        !isEditMode && createFunc(createData);
        isEditMode && editFunc(createData);
    };

    const getDefaultFields = async () => {
        setIsModalReady(false);
        const response = await getLeadGoogleFields();

        if (response.status)
            setFields(
                response?.data?.map(({ api_value }) => {
                    return {
                        field: {
                            value: api_value,
                        },
                        disabled: true,
                        disabledTooltip:
                            "Default fields can not be removed and edit, still you are able to add new fields.",
                    };
                }),
            );
        setIsModalReady(true);
    };

    const getSourceDetail = async () => {
        setIsModalReady(false);
        const response = await getLeadGoogleSourceDetail(data?.id);

        if (response.status) {
            setFields([
                ...response?.data?.fields?.standart?.map(({ api_value }) => {
                    return {
                        field: {
                            value: api_value,
                        },
                        disabled: true,
                        disabledTooltip:
                            "Default fields can not be removed and edit, still you are able to add new fields.",
                    };
                }),
                ...response?.data?.fields?.user_added?.map(({ api_value }) => {
                    return {
                        field: {
                            value: api_value,
                        },
                    };
                }),
            ]);
            setName(response.data?.name);
        }
        setIsModalReady(true);
    };

    useEffect(() => {
        !isEditMode && getDefaultFields();
        isEditMode && getSourceDetail();
    }, []);

    return (
        <GenericModal
            open
            onClose={handleClose}
            title={isEditMode ? "Edit Google Source" : "Create Google Source"}
            maxWidth="md"
            content={
                <Box sx={{ position: "relative", p: 3 }}>
                    {!isModalReady && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "absolute",
                                inset: 0,
                                zIndex: 1,
                                bgcolor: "rgba(255,255,255,0.8)",
                            }}
                        >
                            <CircularProgress size={40} />
                        </Box>
                    )}

                    <TextField
                        label={language?.trackers_page?.name ?? "Name"}
                        fullWidth
                        value={name}
                        className="text-field-ui-workaround"
                        onChange={(event) => setName(event.target.value)}
                    />

                    {isModalReady && (
                        <Box sx={{ mt: 3 }}>
                            <DynamicFieldTable
                                fieldsConfiguration={[
                                    {
                                        label: "Field",
                                        name: "field",
                                        variant: "TEXTBOX",
                                    },
                                ]}
                                fields={fields}
                                setFields={setFields}
                                isScrollable={false}
                                maxHeight={null}
                            />
                        </Box>
                    )}
                </Box>
            }
            footer={
                <CreationFooter
                    onCancel={handleClose}
                    cancelLabel={language?.buttons?.cancel ?? "Cancel"}
                    onSubmit={handleClickCreateBtn}
                    submitLabel={isEditMode ? "Save" : "Create"}
                    isSubmitDisabled={!isModalReady}
                    isSubmitLoading={isLoading}
                    isCancelDisabled={isLoading}
                />
            }
            footerSx={creationFooterSx}
        />
    );
};

export default AddEditGoogleSource;
